import { isEqual } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { UsePayloadSearchDataT, UsePayloadSearchPropsT } from './types';
import { PayloadFilterT, PayloadPagingT, PayloadSortT } from '../../types';

export const usePayloadSearch = <
  F extends Record<string, unknown> = PayloadFilterT,
  S extends Record<string, unknown> = PayloadSortT,
>({
  initFilter,
  initPaging,
  initSort,
}: UsePayloadSearchPropsT<F, S>): UsePayloadSearchDataT<F, S> => {
  const payloadSearchStateRef = useRef({
    isNeedSkipPagingByObserver: false,
    isFirstRender: true,
  });

  const [payload, setPayload] = useState<{
    paging: PayloadPagingT;
    filter: Partial<F>;
    sort: Partial<S>;
  }>({
    paging: initPaging || {
      page: 1,
      limit: 50,
    },
    filter: initFilter || {},
    sort: initSort || {},
  });

  const decrementPage = useCallback(() => {
    setPayload((prev) => {
      payloadSearchStateRef.current.isNeedSkipPagingByObserver = false;

      const { paging, ...rest } = prev;

      if (paging.page <= 1) return prev;

      return { ...rest, paging: { ...paging, page: paging.page - 1 } };
    });
  }, []);

  const increasePage = useCallback(() => {
    setPayload((prev) => {
      payloadSearchStateRef.current.isNeedSkipPagingByObserver = false;

      const { paging, ...rest } = prev;

      return {
        ...rest,
        paging: { ...paging, page: paging.page + 1 },
      };
    });
  }, []);

  const updatePaging = useCallback((updatedPaging: Partial<PayloadPagingT>) => {
    payloadSearchStateRef.current.isNeedSkipPagingByObserver = true;

    setPayload(({ paging, ...rest }) => ({
      ...rest,
      paging: { ...paging, ...updatedPaging },
    }));
  }, []);

  const updateFilter = useCallback((updatedFilter: Partial<F>) => {
    payloadSearchStateRef.current.isNeedSkipPagingByObserver = true;

    setPayload(({ filter, paging, ...rest }) => {
      const nextFilter = { ...filter, ...updatedFilter };

      if (isEqual(filter, nextFilter) && paging.page === 1) {
        return { ...rest, paging, filter };
      }

      return {
        ...rest,
        paging: { ...paging, page: 1 },
        filter: nextFilter,
      };
    });
  }, []);

  const updateSort = useCallback((updatedSort: Partial<S>) => {
    payloadSearchStateRef.current.isNeedSkipPagingByObserver = true;

    setPayload(({ paging, sort, ...rest }) => ({
      ...rest,
      paging: { ...paging, page: 1 },
      sort: { ...sort, ...updatedSort },
    }));
  }, []);

  const putPayload = useCallback(
    (newData: {
      paging?: PayloadPagingT;
      filter?: Partial<F>;
      sort?: Partial<S>;
    }) =>
      setPayload(() => ({
        paging: newData.paging ||
          initPaging || {
            page: 1,
            limit: 50,
          },
        filter: newData.filter || {},
        sort: newData.sort || {},
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const props: UsePayloadSearchDataT<F, S> = useMemo(() => {
    return {
      payload,
      updatePaging,
      updateFilter,
      updateSort,
      increasePage,
      decrementPage,
      putPayload,
      payloadSearchStateRef,
    };
  }, [
    payload,
    updatePaging,
    updateFilter,
    updateSort,
    increasePage,
    decrementPage,
    putPayload,
    payloadSearchStateRef,
  ]);

  return props;
};
